import React, { createContext, useContext, useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { fetchApi } from "../api/client";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const { accounts } = useMsal();
    const [authData, setAuthData] = useState(null);
    const [loading, setLoading] = useState(true);
    const account = accounts[0];

    useEffect(() => {
        if (!account) {
            setAuthData(null);
            setLoading(false);
            return;
        }

        // Check sessionStorage cache first
        const cached = sessionStorage.getItem("auth_me");
        if (cached) {
            try {
                const parsed = JSON.parse(cached);
                if (parsed.email === account.username && Date.now() - parsed._ts < 5 * 60 * 1000) {
                    setAuthData(parsed);
                    setLoading(false);
                    // Still refresh in background
                    fetchAuthMe();
                    return;
                }
            } catch { /* ignore */ }
        }

        fetchAuthMe();

        async function fetchAuthMe() {
            try {
                const data = await fetchApi("/auth/me");
                if (data.authenticated) {
                    data._ts = Date.now();
                    sessionStorage.setItem("auth_me", JSON.stringify(data));
                    setAuthData(data);
                } else {
                    setAuthData(null);
                }
            } catch (err) {
                console.error('[auth] Failed to load user:', err.message);
                sessionStorage.removeItem("auth_me"); // clear stale cache on failure
            }
            setLoading(false);
        }
    }, [account]);

    const hasPermission = (key) => {
        if (!authData || !authData.permissions) return false;
        return authData.permissions.includes(key);
    };

    const hasAnyPermission = (...keys) => {
        if (!authData || !authData.permissions) return false;
        return keys.some((k) => authData.permissions.includes(k));
    };

    const refreshAuth = async () => {
        try {
            const data = await fetchApi("/auth/me");
            if (data.authenticated) {
                data._ts = Date.now();
                sessionStorage.setItem("auth_me", JSON.stringify(data));
                setAuthData(data);
            }
        } catch {
            // Keep existing authData — don't lose permissions on transient failures
        }
    };

    return (
        <AuthContext.Provider value={{ user: authData, loading, hasPermission, hasAnyPermission, refreshAuth }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be inside AuthProvider");
    return ctx;
}
